import { useCallback, useState } from "react";
import {
  Trophy,
  TrendUp,
  Lock,
  CurrencyDollar,
  GitBranch,
  Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { refreshAroundWinner } from "../lib/api";
import SignificanceStatus from "./SignificanceStatus";
import LogConversionDialog from "./LogConversionDialog";
import ConversionSetupDialog from "./ConversionSetupDialog";

function VariantRow({
  variant,
  isClickWinner,
  isCvrWinner,
  isRevenueWinner,
  maxClicks,
  totalClicks,
  onLog,
}) {
  const barPct = totalClicks > 0 ? (variant.clicks / maxClicks) * 100 : 0;
  return (
    <div className="fade-up" data-testid={`variant-row-${variant.variant_id}`}>
      <div className="flex items-center justify-between mb-1.5 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] text-zinc-500 w-6 flex-shrink-0">
            {variant.letter || ""}
          </span>
          {isClickWinner && (
            <Trophy
              size={13}
              weight="fill"
              className="text-[#ccff00] flex-shrink-0"
              title="Winner by clicks"
            />
          )}
          <span className="text-sm text-zinc-200 truncate">
            {variant.headline}
          </span>
        </div>
        <div className="font-mono text-xs text-zinc-300 flex-shrink-0 flex items-center gap-2">
          <button
            data-testid={`log-conversion-${variant.variant_id}`}
            onClick={() => onLog(variant)}
            className="p-1 hover:text-[#ccff00] transition-colors"
            title="Log conversion"
          >
            <Plus size={12} weight="bold" />
          </button>
          <span className={isClickWinner ? "text-[#ccff00] font-bold" : ""}>
            {variant.clicks}
          </span>
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-600">{variant.share}%</span>
        </div>
      </div>
      <div className="h-1.5 bg-[#121212] border border-white/5 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${barPct}%`,
            backgroundColor: isClickWinner ? "#ccff00" : "#3f3f46",
          }}
        />
      </div>
      {/* Conversions + revenue sub-row */}
      <div className="mt-1.5 flex items-center justify-between font-mono text-[10.5px] gap-3">
        <div className="flex items-center gap-3 text-zinc-500">
          <span className="flex items-center gap-1">
            {isCvrWinner && (
              <Trophy
                size={10}
                weight="fill"
                className="text-cyan-300"
                title="Winner by CVR"
              />
            )}
            <span
              className={
                isCvrWinner ? "text-cyan-300 font-bold" : "text-zinc-400"
              }
              data-testid={`variant-cvr-${variant.variant_id}`}
            >
              {variant.cvr?.toFixed(2) || "0.00"}% CVR
            </span>
          </span>
          <span className="flex items-center gap-1">
            {isRevenueWinner && (
              <Trophy
                size={10}
                weight="fill"
                className="text-pink-400"
                title="Winner by revenue"
              />
            )}
            <span
              className={
                isRevenueWinner
                  ? "text-pink-400 font-bold"
                  : "text-zinc-400"
              }
              data-testid={`variant-revenue-${variant.variant_id}`}
            >
              ${variant.revenue?.toFixed(2) || "0.00"}
            </span>
          </span>
        </div>
        <div className="text-zinc-500">
          <span data-testid={`variant-conv-count-${variant.variant_id}`}>
            {variant.conversions || 0}
          </span>{" "}
          conv
        </div>
      </div>
    </div>
  );
}

export default function ABLeaderboard({
  performance,
  activePlatform,
  adSetId,
  tone = "energetic",
  audience = "",
  onAdSetUpdate,
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [logTarget, setLogTarget] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [winnerMetric, setWinnerMetric] = useState("clicks");
  const active =
    performance?.find((p) => p.platform === activePlatform) ||
    performance?.[0];

  const handleRefresh = useCallback(async () => {
    if (!adSetId || !active) return;
    setRefreshing(true);
    try {
      const updated = await refreshAroundWinner(adSetId, {
        platform: active.platform,
        tone,
        audience,
        winner_metric: winnerMetric,
      });
      toast.success(
        `${active.platform.toUpperCase()} · locked winner by ${winnerMetric}. 3 fresh variants generated.`,
      );
      onAdSetUpdate?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [adSetId, active, tone, audience, winnerMetric, onAdSetUpdate]);

  if (!performance?.length || !active) return null;

  const maxClicks = Math.max(1, ...active.variants.map((v) => v.clicks));
  const sigReached = active.significance_reached;

  return (
    <div
      className="bg-[#0a0a0a] border border-white/10 p-6"
      data-testid="ab-leaderboard"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="label-mono">A/B leaderboard</div>
          <div className="font-display text-lg font-bold mt-1">
            {active.platform.toUpperCase()} · {active.total_clicks} click
            {active.total_clicks !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="open-conversion-setup"
            onClick={() => setSetupOpen(true)}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
            title="Setup conversion tracking"
          >
            <GitBranch size={12} weight="fill" />
            setup
          </button>
          <TrendUp size={22} className="text-[#ccff00]" weight="duotone" />
        </div>
      </div>

      {/* Revenue / conversions summary */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <div
          className="bg-[#121212] border border-white/5 p-3"
          data-testid="platform-conversions"
        >
          <div className="label-mono text-[9px]">conversions</div>
          <div className="font-display text-2xl font-bold text-cyan-300 mt-1">
            {active.total_conversions || 0}
          </div>
        </div>
        <div
          className="bg-[#121212] border border-white/5 p-3"
          data-testid="platform-revenue"
        >
          <div className="label-mono text-[9px]">revenue</div>
          {(() => {
            const map = active.total_revenue_by_currency || {};
            const entries = Object.entries(map).filter(([, v]) => v > 0);
            if (entries.length === 0) {
              return (
                <div className="font-display text-2xl font-bold text-pink-400 mt-1">
                  $0.00
                </div>
              );
            }
            if (entries.length === 1) {
              const [ccy, amt] = entries[0];
              return (
                <div className="font-display text-2xl font-bold text-pink-400 mt-1">
                  {ccy === "USD" ? "$" : ""}
                  {amt.toFixed(2)}
                  {ccy !== "USD" && (
                    <span className="text-xs ml-1 font-mono text-zinc-400">
                      {ccy}
                    </span>
                  )}
                </div>
              );
            }
            // multi-currency: stacked rows + USD-consolidated footer
            entries.sort((a, b) => b[1] - a[1]);
            return (
              <div className="space-y-0.5 mt-1" data-testid="platform-revenue-multi">
                {entries.map(([ccy, amt]) => (
                  <div
                    key={ccy}
                    className="font-display text-lg font-bold text-pink-400 leading-tight"
                  >
                    {amt.toFixed(2)}
                    <span className="text-[10px] ml-1 font-mono text-zinc-400">
                      {ccy}
                    </span>
                  </div>
                ))}
                {typeof active.total_revenue_usd === "number" && (
                  <div
                    className="font-mono text-[10px] text-zinc-500 pt-1 border-t border-white/5 mt-1"
                    data-testid="platform-revenue-usd"
                  >
                    ≈ ${active.total_revenue_usd.toFixed(2)} USD
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <SignificanceStatus
        sigReached={sigReached}
        confidence={active.confidence || 0}
        totalClicks={active.total_clicks}
        minClicks={active.min_clicks_required || 30}
      />

      <div className="space-y-3">
        {active.variants.map((v) => (
          <VariantRow
            key={v.variant_id}
            variant={v}
            isClickWinner={v.variant_id === active.winner_by_clicks}
            isCvrWinner={v.variant_id === active.winner_by_cvr}
            isRevenueWinner={v.variant_id === active.winner_by_revenue}
            maxClicks={maxClicks}
            totalClicks={active.total_clicks}
            onLog={setLogTarget}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-3 font-mono text-[9px] text-zinc-500 uppercase tracking-wider">
        <span className="flex items-center gap-1">
          <Trophy size={9} weight="fill" className="text-[#ccff00]" /> clicks
        </span>
        <span className="flex items-center gap-1">
          <Trophy size={9} weight="fill" className="text-cyan-300" /> cvr
        </span>
        <span className="flex items-center gap-1">
          <Trophy size={9} weight="fill" className="text-pink-400" /> revenue
        </span>
      </div>

      {sigReached && (
        <>
          <div className="mt-4 flex items-center gap-1 border border-white/10 p-1 bg-[#050505]">
            {[
              { k: "clicks", label: "clicks", color: "#ccff00" },
              { k: "cvr", label: "cvr", color: "#7dd3fc" },
              { k: "revenue", label: "revenue", color: "#f472b6" },
            ].map((m) => (
              <button
                key={m.k}
                data-testid={`winner-metric-${m.k}`}
                onClick={() => setWinnerMetric(m.k)}
                className={`flex-1 font-mono text-[10px] uppercase tracking-wider py-1.5 px-2 transition-colors ${
                  winnerMetric === m.k
                    ? "text-black font-bold"
                    : "text-zinc-500 hover:text-white"
                }`}
                style={{
                  backgroundColor:
                    winnerMetric === m.k ? m.color : "transparent",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            data-testid={`refresh-around-winner-${active.platform}`}
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-2 w-full bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-3 px-4 flex items-center justify-center gap-2 transition-colors"
          >
            <Lock size={14} weight="fill" />
            {refreshing
              ? "generating fresh variants…"
              : `Lock winner (${winnerMetric}) & refresh variants`}
          </button>
        </>
      )}

      {active.locked_variant_id && (
        <div className="mt-3 font-mono text-[10px] text-zinc-500 flex items-center gap-1.5">
          <Lock size={10} />
          previous winner locked · {active.locked_variant_id.slice(0, 8)}
        </div>
      )}

      {active.total_clicks === 0 && active.total_conversions === 0 && (
        <div className="mt-5 pt-4 border-t border-white/5 font-mono text-[10px] text-zinc-500 leading-relaxed">
          Share the tracker URLs above. As clicks + conversions come in, winning
          variants surface here automatically.
        </div>
      )}

      {active.total_conversions === 0 && active.total_clicks > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5 font-mono text-[10px] text-zinc-500 leading-relaxed flex items-start gap-2">
          <CurrencyDollar
            size={12}
            weight="fill"
            className="text-[#ccff00] mt-0.5 flex-shrink-0"
          />
          <span>
            no conversions yet — drop the pixel or log a manual sale to rank
            variants by <span className="text-[#ccff00]">actual revenue</span>{" "}
            instead of just clicks.
          </span>
        </div>
      )}

      <LogConversionDialog
        open={!!logTarget}
        onOpenChange={(o) => !o && setLogTarget(null)}
        adSetId={adSetId}
        platform={active.platform}
        variantId={logTarget?.variant_id}
        variantLetter={logTarget?.letter}
      />
      <ConversionSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        adSetId={adSetId}
      />
    </div>
  );
}
